// 脚注フォントサイズ動的計算（カード高さ比率方式 v3）
// 要件: 260419_脚注フォントサイズ動的計算_要件詳細.md
//
// === 設計方針（2026-05-01 v3改修） ===
//
// 【背景】v2 までは「要素%は全画面共通(skit-listening 66vh基準)」「画面ごとに違うのは
// cardVh のみ」という設計だったため、cardVh が小さい画面（manual-test=50vh 等）では
// 全要素が連動して小さくなり、phraseNumber が 15.5px と読めないサイズになっていた。
// また、画像表示有り画面でも cardVh が同じため、画像分の縦領域を引かず脚注がはみ出していた。
//
// 【v3 の方針】
//   1. 目標フォント px を「画面によらず同じ見た目サイズ」に揃える
//      - モバイル基準値: phraseNumber=21px, keyPhrase=23px, footnoteText=14.5px 等
//      - 各画面の cardVh から「目標pxを実現するratio」を逆算して、画面ごとに異なる
//        ratios として保持する（SCREEN_LAYOUTS）
//   2. 画像表示の有無を考慮（モバイルのみ）
//      - imageType: "none" / "normal"(160px画像) / "large"(280px画像 = 子供名詞)
//      - 画像表示時は cardHeightPx から画像占有分を引いてから ratio を掛ける
//      - PC版は画像オーバーレイ(横並び)なので縦方向影響なし → 補正なし
//   3. footnoteText は「最長脚注ケースでもはみ出さない」サイズ（14.5px）に下げる
//      - 親子スキット6章F7 で 4行101pxはみ出していた問題に対応
//
// 【公式】
//   px = (cardHeightPx - imageOccupyPx) × ratio
//      where cardHeightPx = innerHeight × cardVh / 100
//
// 【キャッシュ】schemaVersion=3 に更新。古い v2 キャッシュは自動的に再計算される。

// --- 目標フォント px（基準: iPhone 14 = 390×844、cardVh=68 を持つ画面で実測） ---
// 各画面・各レイアウトでこの目標値を実現するように ratios を逆算する
const TARGET_FONT_PX_MOBILE = {
  phraseNumber: 21,
  questionNumber: 21,
  subtitle: 14,
  keyPhrase: 23,
  skitDialog: 23,
  footnoteLabel: 16.5,
  footnoteText: 17,
};

// 画像表示時はカード内コンテンツ密度が上がるため、脚注系を小さく抑える
// 親子スキット6章F7（裏面に4行脚注）でカード下端を超えないようにするための実測ベース調整
// #635 で +1.5px拡大（11.5→13）。同章F7のはみ出し再発防止のため画像なし版より控えめ。
const TARGET_FONT_PX_MOBILE_WITH_IMAGE = {
  phraseNumber: 21,
  questionNumber: 21,
  subtitle: 12,
  keyPhrase: 20,
  skitDialog: 18,
  footnoteLabel: 13,
  footnoteText: 13,
};

// PC版は v2 互換維持（skit-listening 66vh基準・iPad mini 768×1024 想定）
const TARGET_FONT_PX_DESKTOP = {
  phraseNumber: 51.2,    // 1024 × 0.66 × 0.0758 ≒ 51.2（v2 ratio 0.0758 を 1024×0.66 で再現）
  questionNumber: 51.2,
  subtitle: 25.6,
  keyPhrase: 51.2,
  skitDialog: 51.2,
  footnoteLabel: 25.6,
  footnoteText: 26.9,    // v2 ratio 0.0398 × 1024 × 0.66
};

// --- 画面別オーバーライド（v3.1 / 2026-05-02 追加） ---
// 画面ごとに目標pxを上書きできる。指定したキーだけが上書きされ、
// 未指定のキーは上記デフォルト（TARGET_FONT_PX_*）の値を使用する。
// 画面そのものが未登録ならデフォルトを丸ごと使う。

// PC版オーバーライド（画面ごと）
const TARGET_FONT_PX_DESKTOP_OVERRIDES = {
  // skit-pronunciation-test: 脚注はみ出し対応のためカード内要素を縮小
  // - phraseNumber 51.2→41.2（-10px）
  // - questionNumber 51.2→41.2（-10px）
  // - skitDialog 51.2→35.2（-16px）
  "skit-pronunciation-test": {
    phraseNumber: 41.2,
    questionNumber: 41.2,
    skitDialog: 35.2,
  },
};

// モバイル版オーバーライド（画像なし）
const TARGET_FONT_PX_MOBILE_OVERRIDES = {
};

// モバイル版オーバーライド（画像あり）
const TARGET_FONT_PX_MOBILE_WITH_IMAGE_OVERRIDES = {
};

// 画面ごとのターゲット値を解決（デフォルト + 画面別オーバーライド）
function resolveTarget(defaultTarget, overrides, screen) {
  const override = overrides[screen];
  if (!override) return defaultTarget;
  return { ...defaultTarget, ...override };
}

// 各練習画面のカード領域が占める高さ比（vh / dvh）
// 画面のheader/footer/panel等を差し引いた残りの、カード実表示可能高さ（page.js実測）
const CARD_VH_PER_SCREEN = {
  // --- skit画面 ---
  "skit-listening":           { desktop: 66, mobile: 68 },
  "skit-manual-test":         { desktop: 50, mobile: 50 },
  "skit-pronunciation-test":  { desktop: 58, mobile: 58 },
  "skit-retry-challenge":     { desktop: 54, mobile: 54 },
  // --- 非skit画面 ---
  "listening":                { desktop: 66, mobile: 68 },
  "manual-test":              { desktop: 50, mobile: 50 },
  "pronunciation-test":       { desktop: 66, mobile: 68 },
  "retry-challenge":          { desktop: 66, mobile: 72 },
  "audio-only-manual":        { desktop: 66, mobile: 68 },
};

// 基準innerHeight（ratio 逆算用）
const REFERENCE_HEIGHT_MOBILE = 844;   // iPhone 14
const REFERENCE_HEIGHT_DESKTOP = 1024; // iPad mini相当

// 画像占有px（モバイル版のみ。imageUrl 表示時にカード縦領域から減算）
// SkitCard / testCard / listeningCard の sm:hidden 内 maxHeight + my-2(=16px)
const IMAGE_OCCUPY_PX = {
  none: 0,
  normal: 176,  // 160 + 16(margin)
  large: 296,   // 280 + 16(margin) - 子供名詞用
};

// PC版脚注の行高（px固定値）。モバイルは fontSize × 1.45（画像なし）/ 1.3（画像あり）を採用
const FOOTNOTE_LINE_HEIGHT_DESKTOP = 25;
const FOOTNOTE_LINE_HEIGHT_MOBILE_RATIO = 1.45;
const FOOTNOTE_LINE_HEIGHT_MOBILE_RATIO_WITH_IMAGE = 1.3;

// --- ratios 逆算ヘルパー ---
// targetPx = (REFERENCE_HEIGHT × cardVh/100 - imageOccupyPx) × ratio
// → ratio = targetPx / (REFERENCE_HEIGHT × cardVh/100 - imageOccupyPx)
function buildRatios(target, refHeight, cardVh, imageOccupyPx) {
  const effectiveCardHeight = refHeight * cardVh / 100 - imageOccupyPx;
  if (effectiveCardHeight <= 0) {
    // 画像でカードが埋まりきるケースは安全側でratioを0扱い
    const zeroed = {};
    for (const k of Object.keys(target)) zeroed[k] = 0;
    return zeroed;
  }
  const ratios = {};
  for (const key of Object.keys(target)) {
    ratios[key] = target[key] / effectiveCardHeight;
  }
  return ratios;
}

// --- SCREEN_LAYOUTS 自動生成 ---
// SCREEN_LAYOUTS[screen] = { desktop, mobile, mobileWithImage, mobileWithLargeImage }
// 各エントリに { cardVh, ratios, imageOccupyPx } を持たせる
function buildScreenLayouts() {
  const layouts = {};
  for (const screen of Object.keys(CARD_VH_PER_SCREEN)) {
    const def = CARD_VH_PER_SCREEN[screen];
    const desktopTarget = resolveTarget(TARGET_FONT_PX_DESKTOP, TARGET_FONT_PX_DESKTOP_OVERRIDES, screen);
    const mobileTarget = resolveTarget(TARGET_FONT_PX_MOBILE, TARGET_FONT_PX_MOBILE_OVERRIDES, screen);
    const mobileWithImageTarget = resolveTarget(TARGET_FONT_PX_MOBILE_WITH_IMAGE, TARGET_FONT_PX_MOBILE_WITH_IMAGE_OVERRIDES, screen);
    layouts[screen] = {
      desktop: {
        cardVh: def.desktop,
        imageOccupyPx: 0,
        ratios: buildRatios(desktopTarget, REFERENCE_HEIGHT_DESKTOP, def.desktop, 0),
      },
      mobile: {
        cardVh: def.mobile,
        imageOccupyPx: 0,
        ratios: buildRatios(mobileTarget, REFERENCE_HEIGHT_MOBILE, def.mobile, 0),
      },
      mobileWithImage: {
        cardVh: def.mobile,
        imageOccupyPx: IMAGE_OCCUPY_PX.normal,
        ratios: buildRatios(mobileWithImageTarget, REFERENCE_HEIGHT_MOBILE, def.mobile, IMAGE_OCCUPY_PX.normal),
      },
      mobileWithLargeImage: {
        cardVh: def.mobile,
        imageOccupyPx: IMAGE_OCCUPY_PX.large,
        ratios: buildRatios(mobileWithImageTarget, REFERENCE_HEIGHT_MOBILE, def.mobile, IMAGE_OCCUPY_PX.large),
      },
    };
  }
  return layouts;
}

export const SCREEN_LAYOUTS = buildScreenLayouts();

// 画面サイズからキー生成
export function makeSizeKey(innerWidth, innerHeight) {
  return innerWidth + "x" + innerHeight;
}

// imageType 解決: "none" / "normal" / "large"
function resolveImageType(hasImage, isChildNoun) {
  if (!hasImage) return "none";
  return isChildNoun ? "large" : "normal";
}

// レイアウトキー解決
function resolveLayoutKey(isDesktopLayout, imageType) {
  if (isDesktopLayout) return "desktop";  // PC版は画像有無で変わらない
  if (imageType === "large") return "mobileWithLargeImage";
  if (imageType === "normal") return "mobileWithImage";
  return "mobile";
}

// 1画面分のフォントサイズを計算
// 公式: px = (innerHeight × cardVh/100 - imageOccupyPx) × ratio
export function calculateScreenSizes(innerWidth, innerHeight, screen, imageType = "none") {
  const layouts = SCREEN_LAYOUTS[screen];
  if (!layouts) return null;
  const isDesktopLayout = innerWidth >= 640;
  const layoutKey = resolveLayoutKey(isDesktopLayout, imageType);
  const layout = layouts[layoutKey];
  if (!layout) return null;

  const cardHeightPx = innerHeight * layout.cardVh / 100;
  const effectiveHeightPx = cardHeightPx - layout.imageOccupyPx;

  const result = {};
  for (const key of Object.keys(layout.ratios)) {
    const px = effectiveHeightPx * layout.ratios[key];
    result[key] = Math.round(px * 10) / 10;
  }
  if (isDesktopLayout) {
    result.footnoteLineHeight = FOOTNOTE_LINE_HEIGHT_DESKTOP;
  } else if (result.footnoteText) {
    const ratio = imageType === "none" ? FOOTNOTE_LINE_HEIGHT_MOBILE_RATIO : FOOTNOTE_LINE_HEIGHT_MOBILE_RATIO_WITH_IMAGE;
    result.footnoteLineHeight = Math.round(result.footnoteText * ratio * 10) / 10;
  }
  return result;
}

// 全練習画面分を計算（全 imageType の組み合わせ込み）
export function calculateAllScreens(innerWidth, innerHeight) {
  const result = {};
  for (const screen of Object.keys(SCREEN_LAYOUTS)) {
    result[screen] = {
      none:   calculateScreenSizes(innerWidth, innerHeight, screen, "none"),
      normal: calculateScreenSizes(innerWidth, innerHeight, screen, "normal"),
      large:  calculateScreenSizes(innerWidth, innerHeight, screen, "large"),
    };
  }
  return result;
}

const SCHEMA_VERSION = 7;

// localStorageキャッシュ確認＋未キャッシュなら計算・保存
// 章ボタン / 再挑戦ボタン押下時に呼び出す
export function ensureFontSizeCache() {
  if (typeof window === "undefined") return null;
  try {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const sizeKey = makeSizeKey(w, h);
    const cacheKey = "font-size-cache-" + sizeKey;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.schemaVersion === SCHEMA_VERSION) {
          return { key: sizeKey, sizes: parsed, fromCache: true };
        }
      } catch (e) {
        // 壊れたキャッシュは無視して再計算
      }
    }
    const sizes = calculateAllScreens(w, h);
    const payload = {
      schemaVersion: SCHEMA_VERSION,
      sizes,
      innerWidth: w,
      innerHeight: h,
      calculatedAt: new Date().toISOString(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(payload));
    return { key: sizeKey, sizes: payload, fromCache: false };
  } catch (e) {
    console.warn("[fontSizeCalculator] ensureFontSizeCache failed:", e);
    return null;
  }
}

// 練習画面側で読み取り
// imageType = "none" / "normal" / "large"
export function readFontSizeFor(screen, imageType = "none") {
  if (typeof window === "undefined") return null;
  try {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const cacheKey = "font-size-cache-" + makeSizeKey(w, h);
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    const parsed = JSON.parse(cached);
    if (parsed.schemaVersion !== SCHEMA_VERSION) return null;
    if (!parsed.sizes || !parsed.sizes[screen]) return null;
    const screenSizes = parsed.sizes[screen];
    return screenSizes[imageType] || screenSizes.none || null;
  } catch (e) {
    return null;
  }
}

// 互換用ヘルパー: hasImage / isChildNoun から imageType に変換
export function resolveImageTypeFromProps(hasImage, isChildNoun) {
  return resolveImageType(hasImage, isChildNoun);
}

// デバッグパネル用: 現在の計算パラメータと結果を返す
export function getDebugInfo() {
  if (typeof window === "undefined") return null;
  const w = window.innerWidth;
  const h = window.innerHeight;
  const sizeKey = makeSizeKey(w, h);
  const cacheKey = "font-size-cache-" + sizeKey;
  let cached = null;
  try {
    const raw = localStorage.getItem(cacheKey);
    cached = raw ? JSON.parse(raw) : null;
  } catch (e) {}
  return {
    innerWidth: w,
    innerHeight: h,
    sizeKey,
    cacheKey,
    isDesktopLayout: w >= 640,
    targetFontPxMobile: TARGET_FONT_PX_MOBILE,
    targetFontPxMobileWithImage: TARGET_FONT_PX_MOBILE_WITH_IMAGE,
    targetFontPxDesktop: TARGET_FONT_PX_DESKTOP,
    targetFontPxDesktopOverrides: TARGET_FONT_PX_DESKTOP_OVERRIDES,
    targetFontPxMobileOverrides: TARGET_FONT_PX_MOBILE_OVERRIDES,
    targetFontPxMobileWithImageOverrides: TARGET_FONT_PX_MOBILE_WITH_IMAGE_OVERRIDES,
    cardVhPerScreen: CARD_VH_PER_SCREEN,
    imageOccupyPx: IMAGE_OCCUPY_PX,
    referenceHeightMobile: REFERENCE_HEIGHT_MOBILE,
    referenceHeightDesktop: REFERENCE_HEIGHT_DESKTOP,
    footnoteLineHeightDesktop: FOOTNOTE_LINE_HEIGHT_DESKTOP,
    footnoteLineHeightMobileRatio: FOOTNOTE_LINE_HEIGHT_MOBILE_RATIO,
    screenLayouts: SCREEN_LAYOUTS,
    cached,
    formula: "px = (innerHeight × cardVh/100 - imageOccupyPx) × ratio",
    schemaVersion: SCHEMA_VERSION,
  };
}

// === v2 互換エクスポート（既存DebugStoragePanel等から参照されている可能性に備える） ===
export const CARD_HEIGHT_VH = CARD_VH_PER_SCREEN;
